/**
 * Global mapping from API error codes to user-friendly messages.
 * The frontend checks this map when an API response includes a `code` field.
 * Pages can override specific codes via the `overrides` parameter in getErrorMessage().
 */
// Canonical user-safe API error copy.
export const ERROR_MESSAGES: Record<string, string> = {
  // ── Generic ──────────────────────────────────────────────────────
  NOT_FOUND: "That resource doesn't exist or has been removed",
  VALIDATION_ERROR: 'Some fields need fixing — check the form',
  BAD_REQUEST: "Something's wrong with that request",
  UNAUTHORIZED: 'You need to sign in',
  FORBIDDEN: "You don't have permission to do that",
  CONFLICT: 'That conflicts with something that already exists',
  INTERNAL_ERROR: 'Something went wrong on our end — try again',
  RATE_LIMITED: 'Too many requests — slow down a moment',
  SERVICE_UNAVAILABLE: 'That service is temporarily unavailable',
  INVALID_ID: 'Invalid ID format',
  INVALID_TENANT: 'Tenant context is missing or invalid',
  TENANT_REQUIRED: 'Tenant context is required',
  USER_REQUIRED: 'User context is required',
  DECODE_ERROR: "We couldn't read that request — check the data format",
  METHOD_NOT_ALLOWED: "That action isn't supported here",

  // ── Auth ─────────────────────────────────────────────────────────
  AUTH_INVALID_CREDENTIALS: 'Invalid email or password',
  AUTH_MFA_REQUIRED: 'Multi-factor authentication is required',
  AUTH_MFA_EXPIRED: 'Your MFA session has expired — verify again',
  AUTH_ACCOUNT_SUSPENDED: 'This account has been suspended',
  AUTH_SESSION_EXPIRED: 'Your session has expired — sign in again',
  AUTH_REFRESH_FAILED: "Couldn't refresh your session — sign in again",

  // ── Tickets ──────────────────────────────────────────────────────
  TICKETS_NOT_FOUND: "That ticket doesn't exist — it may have been deleted or merged",
  TICKETS_CREATION_FAILED: "Couldn't create the ticket",
  TICKETS_UPDATE_FAILED: "Couldn't update the ticket",
  TICKETS_DELETION_FAILED: "Couldn't delete the ticket",
  TICKETS_INVALID_ID: 'Invalid ticket ID',
  TICKETS_INVALID_NUMBER: 'Invalid ticket number',
  TICKETS_INVALID_PRIORITY: 'Choose a valid priority level',
  TICKETS_INVALID_STATUS: 'Invalid ticket status',
  TICKETS_ASSIGNMENT_FAILED: "Couldn't assign that engineer",
  TICKETS_UNASSIGNMENT_FAILED: "Couldn't unassign the engineer",
  TICKETS_RESOLUTION_FAILED: "Couldn't resolve the ticket",
  TICKETS_STATUS_CHANGE_FAILED: "Couldn't change the ticket status",
  TICKETS_ESCALATION_FAILED: 'Escalation failed — check the escalation rules',
  TICKETS_MERGE_FAILED: "Couldn't merge the tickets",
  TICKETS_MERGE_SAME_TICKET: "You can't merge a ticket with itself",
  TICKETS_ALREADY_CLOSED: 'This ticket is already closed',
  TICKETS_REQUIRED_FIELD_MISSING: 'A required field is missing',
  TICKETS_COMMENT_NOT_FOUND: "That comment doesn't exist",
  TICKETS_COMMENT_INVALID_ID: 'Invalid comment ID',
  TICKETS_COMMENT_CREATION_FAILED: "Couldn't add the comment",
  TICKETS_COMMENT_UPDATE_FAILED: "Couldn't update the comment",
  TICKETS_COMMENT_DELETION_FAILED: "Couldn't delete the comment",
  TICKETS_WATCHER_ADDITION_FAILED: "Couldn't add the watcher",
  TICKETS_WATCHER_REMOVAL_FAILED: "Couldn't remove the watcher",
  TICKETS_TEMPLATE_NOT_FOUND: "That ticket template doesn't exist",
  TICKETS_TEMPLATE_CREATION_FAILED: "Couldn't create the template",
  TICKETS_TEMPLATE_UPDATE_FAILED: "Couldn't update the template",
  TICKETS_TEMPLATE_DELETION_FAILED: "Couldn't delete the template",
  TICKETS_TEMPLATE_APPLICATION_FAILED: "Couldn't apply the template",
  TICKETS_CANNED_RESPONSE_NOT_FOUND: "That canned response doesn't exist",
  TICKETS_CANNED_RESPONSE_CREATION_FAILED: "Couldn't create the canned response",
  TICKETS_CANNED_RESPONSE_UPDATE_FAILED: "Couldn't update the canned response",
  TICKETS_CANNED_RESPONSE_DELETION_FAILED: "Couldn't delete the canned response",

  // ── Work Items ───────────────────────────────────────────────────
  WORKITEM_NOT_FOUND: "That work item doesn't exist",
  WORKITEM_CREATION_FAILED: "Couldn't create the work item",
  WORKITEM_UPDATE_FAILED: "Couldn't update the work item",
  WORKITEM_DELETION_FAILED: "Couldn't delete the work item",
  WORKITEM_STATUS_CHANGE_FAILED: "Couldn't change the work item status",
  WORKITEM_DEPENDENCY_NOT_FOUND: "That dependency doesn't exist",
  WORKITEM_DEPENDENCY_CREATION_FAILED: "Couldn't add the dependency",
  WORKITEM_DEPENDENCY_DELETION_FAILED: "Couldn't remove the dependency",
  WORKITEM_CHECKLIST_ITEM_NOT_FOUND: "That checklist item doesn't exist",
  WORKITEM_CHECKLIST_ITEM_TOGGLE_FAILED: "Couldn't toggle the checklist item",

  // ── Dispatch ─────────────────────────────────────────────────────
  DISPATCH_ENGINEER_NOT_FOUND: "That engineer doesn't exist",
  DISPATCH_ENGINEER_CREATION_FAILED: "Couldn't create the engineer profile",
  DISPATCH_ENGINEER_UPDATE_FAILED: "Couldn't update the engineer profile",
  DISPATCH_ENGINEER_DELETION_FAILED: "Couldn't delete the engineer profile",
  DISPATCH_ENGINEER_SKILL_ADD_FAILED: "Couldn't add the skill",
  DISPATCH_ENGINEER_SKILL_REMOVE_FAILED: "Couldn't remove the skill",
  DISPATCH_ENGINEER_SCHEDULE_UPDATE_FAILED: "Couldn't update the schedule",
  DISPATCH_ENGINEER_AVAILABILITY_CHECK_FAILED: "Couldn't check availability",
  DISPATCH_ENGINEER_LOAD_FETCH_FAILED: "Couldn't load engineer workload",
  DISPATCH_ENGINEER_ASSIGNMENT_FETCH_FAILED: "Couldn't load assignments",
  DISPATCH_ENGINEER_LOCATION_SET_FAILED: "Couldn't set location",
  DISPATCH_ENGINEER_LOCATION_FETCH_FAILED: "Couldn't load location",
  DISPATCH_ENGINEER_STATUS_UPDATE_FAILED: "Couldn't update status",
  DISPATCH_ENGINEER_ON_CALL_SET_FAILED: "Couldn't set on-call status",
  DISPATCH_ENGINEER_ID_REQUIRED: 'Engineer ID is required',
  DISPATCH_SLA_TIER_NOT_FOUND: "That SLA tier doesn't exist",
  DISPATCH_SLA_TIER_CREATION_FAILED: "Couldn't create the SLA tier",
  DISPATCH_SLA_TIER_UPDATE_FAILED: "Couldn't update the SLA tier",
  DISPATCH_SLA_TIER_DELETION_FAILED: "Couldn't delete the SLA tier",
  DISPATCH_SLA_TIER_LIST_FAILED: "Couldn't load SLA tiers",
  DISPATCH_SLA_ASSIGNMENT_FAILED: "Couldn't assign the SLA policy",
  DISPATCH_SLA_ASSIGNMENT_NOT_FOUND: "That SLA assignment doesn't exist",
  DISPATCH_SLA_DEADLINE_CALCULATION_FAILED: "Couldn't calculate SLA deadline",
  DISPATCH_SLA_STATUS_FETCH_FAILED: "Couldn't load SLA status",
  DISPATCH_CUSTOMER_SLA_FETCH_FAILED: "Couldn't load customer SLA",
  DISPATCH_TICKET_ASSIGNMENT_FAILED: "Couldn't assign the ticket",
  DISPATCH_REASSIGNMENT_FAILED: "Couldn't reassign the ticket",
  DISPATCH_WORK_ITEM_ASSIGNMENT_FAILED: "Couldn't assign the work item",
  DISPATCH_ESCALATION_RULE_NOT_FOUND: "That escalation rule doesn't exist",
  DISPATCH_ESCALATION_RULE_CREATION_FAILED: "Couldn't create the escalation rule",
  DISPATCH_ESCALATION_RULE_UPDATE_FAILED: "Couldn't update the escalation rule",
  DISPATCH_ESCALATION_RULE_DELETION_FAILED: "Couldn't delete the escalation rule",
  DISPATCH_ESCALATION_RULE_LIST_FAILED: "Couldn't load escalation rules",
  DISPATCH_ESCALATION_PROCESSING_FAILED: 'Escalation processing failed',
  DISPATCH_QUEUE_NOT_FOUND: "That dispatch queue doesn't exist",
  DISPATCH_QUEUE_CREATION_FAILED: "Couldn't create the queue",
  DISPATCH_QUEUE_UPDATE_FAILED: "Couldn't update the queue",
  DISPATCH_QUEUE_ASSIGNEE_ADD_FAILED: "Couldn't add assignee to queue",
  DISPATCH_QUEUE_SKILL_ADD_FAILED: "Couldn't add skill to queue",
  DISPATCH_SKILL_NOT_FOUND: "That skill doesn't exist",
  DISPATCH_SKILL_CREATION_FAILED: "Couldn't create the skill",
  DISPATCH_SKILL_LIST_FAILED: "Couldn't load skills",
  DISPATCH_HOLIDAY_CREATION_FAILED: "Couldn't create the holiday",
  DISPATCH_HOLIDAY_DELETION_FAILED: "Couldn't delete the holiday",
  DISPATCH_HOLIDAY_LIST_FAILED: "Couldn't load holidays",
  DISPATCH_ON_CALL_ENTRY_CREATION_FAILED: "Couldn't create the on-call entry",
  DISPATCH_ON_CALL_ENTRY_DELETION_FAILED: "Couldn't delete the on-call entry",
  DISPATCH_ON_CALL_SCHEDULE_FETCH_FAILED: "Couldn't load on-call schedule",
  DISPATCH_ON_CALL_CURRENT_FETCH_FAILED: "Couldn't load current on-call",
  DISPATCH_SCHEDULE_OVERRIDE_ADD_FAILED: "Couldn't add schedule override",
  DISPATCH_SCHEDULE_OPTIMIZATION_FAILED: 'Schedule optimization failed',
  DISPATCH_NOTIFICATION_SEND_FAILED: "Couldn't send notification",
  DISPATCH_REMINDER_CREATION_FAILED: "Couldn't create the reminder",
  DISPATCH_REMINDER_DISMISSAL_FAILED: "Couldn't dismiss the reminder",
  DISPATCH_REMINDER_PENDING_FETCH_FAILED: "Couldn't load pending reminders",
  DISPATCH_PROXIMITY_CALCULATION_FAILED: "Couldn't calculate proximity",
  DISPATCH_BUSINESS_DAY_CHECK_FAILED: "Couldn't check business day",
  DISPATCH_NEXT_BUSINESS_DAY_FETCH_FAILED: "Couldn't determine next business day",
  DISPATCH_DURATION_ESTIMATION_FAILED: "Couldn't estimate duration",
  DISPATCH_BOARD_DATA_FETCH_FAILED: "Couldn't load dispatch board data",
  DISPATCH_TEAM_LOAD_FETCH_FAILED: "Couldn't load team workload",
  DISPATCH_METRICS_RECORD_FAILED: "Couldn't record metrics",
  DISPATCH_METRICS_FETCH_FAILED: "Couldn't load metrics",
  DISPATCH_INTERACTION_TIMELINE_FETCH_FAILED: "Couldn't load interaction timeline",
  DISPATCH_INTERACTION_RECORD_FAILED: "Couldn't record interaction",
  DISPATCH_COMMUNICATION_PREFERENCES_FETCH_FAILED: "Couldn't load communication preferences",
  DISPATCH_COMMUNICATION_PREFERENCES_UPDATE_FAILED: "Couldn't update communication preferences",
  DISPATCH_FEEDBACK_REQUEST_FAILED: "Couldn't request feedback",
  DISPATCH_FEEDBACK_STATS_FETCH_FAILED: "Couldn't load feedback stats",
  DISPATCH_MEETING_CREATION_FAILED: "Couldn't create the meeting",
  DISPATCH_MEETING_FETCH_FAILED: "Couldn't load the meeting",
  DISPATCH_MEETING_LIST_FAILED: "Couldn't load meetings",
  DISPATCH_OUTLOOK_SYNC_FAILED: 'Outlook sync failed',
  DISPATCH_TEAMS_SYNC_FAILED: 'Teams sync failed',
  DISPATCH_CUSTOMER_LOCATION_NOT_FOUND: 'Customer location not found',
  DISPATCH_CUSTOMER_LOCATION_SET_FAILED: "Couldn't set customer location",
  DISPATCH_ENGINEER_LOCATION_NOT_FOUND: 'Engineer location not found',
  DISPATCH_CONFIG_FETCH_FAILED: "Couldn't load dispatch config",
  DISPATCH_CONFIG_UPDATE_FAILED: "Couldn't update dispatch config",
  DISPATCH_CATEGORY_REQUIRED: 'Category is required',
  DISPATCH_TIME_FORMAT_INVALID: 'Invalid time format',
  DISPATCH_AVAILABLE_ENGINEERS_FETCH_FAILED: "Couldn't load available engineers",
  DISPATCH_EXPIRING_ASSIGNMENTS_FETCH_FAILED: "Couldn't load expiring assignments",
  DISPATCH_EXPIRING_ASSIGNMENTS_EXPIRE_FAILED: "Couldn't expire assignments",
  DISPATCH_PENDING_ASSIGNMENTS_FETCH_FAILED: "Couldn't load pending assignments",
  DISPATCH_AFTER_HOURS_HANDLING_FAILED: 'After-hours handling failed',
  DISPATCH_DUE_REMINDERS_PROCESSING_FAILED: "Couldn't process due reminders",
  DISPATCH_HOLIDAY_SEEDING_FAILED: "Couldn't seed holidays",

  // ── Billing ──────────────────────────────────────────────────────
  BILLING_CONTRACT_NOT_FOUND: "That contract doesn't exist",
  BILLING_CONTRACT_CREATION_FAILED: "Couldn't create the contract",
  BILLING_CONTRACT_UPDATE_FAILED: "Couldn't update the contract",
  BILLING_CONTRACT_DELETION_FAILED: "Couldn't delete the contract",
  BILLING_CONTRACT_ACTIVATION_FAILED: "Couldn't activate the contract",
  BILLING_CONTRACT_CALCULATION_FAILED: "Couldn't calculate contract values",
  BILLING_INVOICE_NOT_FOUND: "That invoice doesn't exist",
  BILLING_INVOICE_CREATION_FAILED: "Couldn't create the invoice",
  BILLING_INVOICE_UPDATE_FAILED: "Couldn't update the invoice",
  BILLING_INVOICE_DELETION_FAILED: "Couldn't delete the invoice",
  BILLING_INVOICE_PDF_GENERATION_FAILED: "Couldn't generate the invoice PDF",
  BILLING_LINE_ITEM_ADDITION_FAILED: "Couldn't add the line item",
  BILLING_LINE_ITEM_DELETION_FAILED: "Couldn't remove the line item",
  BILLING_RATE_SCHEDULE_NOT_FOUND: "That rate schedule doesn't exist",
  BILLING_RATE_SCHEDULE_CREATION_FAILED: "Couldn't create the rate schedule",
  BILLING_RATE_SCHEDULE_UPDATE_FAILED: "Couldn't update the rate schedule",
  BILLING_RATE_SCHEDULE_DELETION_FAILED: "Couldn't delete the rate schedule",
  BILLING_RATE_SCHEDULE_DEFAULT_FETCH_FAILED: "Couldn't load the default rate schedule",
  BILLING_SUMMARY_FETCH_FAILED: "Couldn't load billing summary",
  BILLING_AR_AGING_REPORT_FETCH_FAILED: "Couldn't load AR aging report",
  BILLING_CONTRACT_FETCH_FAILED: "Couldn't load contracts",

  // ── Devices ──────────────────────────────────────────────────────
  DEVICE_NOT_FOUND: "That device doesn't exist",
  DEVICE_CREATION_FAILED: "Couldn't create the device",
  DEVICE_UPDATE_FAILED: "Couldn't update the device",
  DEVICE_DELETION_FAILED: "Couldn't delete the device",
  DEVICE_INVALID_ID: 'Invalid device ID',
  DEVICE_GROUP_NOT_FOUND: "That device group doesn't exist",
  DEVICE_GROUP_CREATION_FAILED: "Couldn't create the device group",
  DEVICE_GROUP_UPDATE_FAILED: "Couldn't update the device group",
  DEVICE_GROUP_DELETION_FAILED: "Couldn't delete the device group",
  DEVICE_GROUP_INVALID_ID: 'Invalid device group ID',
  DEVICE_TAGS_ADD_FAILED: "Couldn't add tags",
  DEVICE_TAGS_REMOVE_FAILED: "Couldn't remove tags",
  DEVICE_METRICS_FETCH_FAILED: "Couldn't load device metrics",
  DEVICE_PROCESSES_FETCH_FAILED: "Couldn't load device processes",
  DEVICE_SERVICES_FETCH_FAILED: "Couldn't load device services",
  DEVICE_STATS_FETCH_FAILED: "Couldn't load device stats",
  DEVICE_LIST_FAILED: "Couldn't load devices",
  DEVICE_SNAPSHOT_NOT_FOUND: "That snapshot doesn't exist",
  DEVICE_SNAPSHOT_FETCH_FAILED: "Couldn't load the snapshot",
  DEVICE_SNAPSHOT_DELETION_FAILED: "Couldn't delete the snapshot",
  DEVICE_SNAPSHOT_TYPE_REQUIRED: 'Snapshot type is required',
  DEVICE_COMMAND_FAILED: 'Command execution failed',
  DEVICE_COMMAND_BROADCAST_FAILED: "Couldn't broadcast the command",
  DEVICE_SCRIPT_REQUIRED: 'Script is required',
  DEVICE_PATH_REQUIRED: 'Path is required',
  DEVICE_FILE_OPERATIONS_FAILED: 'File operation failed',
  DEVICE_NOTE_CREATION_FAILED: "Couldn't add the note",
  DEVICE_NOTE_DELETION_FAILED: "Couldn't delete the note",
  DEVICE_WOL_FAILED: 'Wake-on-LAN failed',
  DEVICE_SERVICE_ACTION_FAILED: 'Service action failed',
  DEVICE_HEARTBEAT_PROCESSING_FAILED: "Couldn't process heartbeat",
  DEVICE_AGENT_REGISTRATION_FAILED: 'Agent registration failed',
  DEVICE_AGENT_DEPLOYMENT_FAILED: 'Agent deployment failed',
  DEVICE_SCAN_INITIATION_FAILED: "Couldn't start the scan",
  DEVICE_INSTALL_INITIATION_FAILED: "Couldn't start the install",
  DEVICE_BACKUP_STATUS_FETCH_FAILED: "Couldn't load backup status",
  DEVICE_NETWORK_DOCUMENTATION_FETCH_FAILED: "Couldn't load network documentation",
  DEVICE_SECURITY_STATUS_UPDATE_FAILED: "Couldn't update security status",
  DEVICE_ROLE_UPDATE_FAILED: "Couldn't update device role",
  DEVICE_ROLE_MARSHAL_FAILED: "Couldn't process device role data",
  DEVICE_SOFTWARE_UPDATE_FAILED: "Couldn't update software info",
  DEVICE_USER_ACCOUNT_UPDATE_FAILED: "Couldn't update user account info",
  DEVICE_EVENT_LOG_UPDATE_FAILED: "Couldn't update event log",
  DEVICE_METADATA_UPDATE_FAILED: "Couldn't update device metadata",
  DEVICE_WIPE_CONFIRMATION_REQUIRED: 'Wipe confirmation is required',
  DEVICE_REQUEST_ENTITY_TOO_LARGE: 'Request payload is too large',
  DEVICE_REGISTRY_WRITE_CLASSIFY_FAILED: "Couldn't classify registry write",

  // ── Customers ────────────────────────────────────────────────────
  CUSTOMER_NOT_FOUND: "That customer doesn't exist",
  CUSTOMER_CREATION_FAILED: "Couldn't create the customer",
  CUSTOMER_UPDATE_FAILED: "Couldn't update the customer",
  CUSTOMER_DELETION_FAILED: "Couldn't delete the customer",
  CUSTOMER_INVALID_ID: 'Invalid customer ID',
  CUSTOMER_ACTIVATION_FAILED: "Couldn't activate the customer",
  CUSTOMER_SUSPENSION_FAILED: "Couldn't suspend the customer",
  CUSTOMER_CHURN_FAILED: "Couldn't process customer churn",
  CUSTOMER_M365_DOMAIN_MISSING: 'M365 domain is not configured for this customer',
  CUSTOMER_M365_TENANT_MISSING: 'M365 tenant is not configured for this customer',
  CUSTOMER_M365_TOKEN_ACQUISITION_FAILED: "Couldn't get M365 token for this customer",
  CUSTOMER_M365_TENANT_RESOLUTION_FAILED: "Couldn't resolve M365 tenant",
  CUSTOMER_HEALTH_SCORE_CALCULATION_FAILED: "Couldn't calculate health score",
  CUSTOMER_STATISTICS_FETCH_FAILED: "Couldn't load customer statistics",
  CUSTOMER_CONTACT_NOT_FOUND: "That contact doesn't exist",
  CUSTOMER_CONTACT_CREATION_FAILED: "Couldn't create the contact",
  CUSTOMER_CONTACT_UPDATE_FAILED: "Couldn't update the contact",
  CUSTOMER_CONTACT_DELETION_FAILED: "Couldn't delete the contact",
  CUSTOMER_CONTACT_INVALID_ID: 'Invalid contact ID',
  CUSTOMER_CONTACT_PRIMARY_SET_FAILED: "Couldn't set primary contact",
  CUSTOMER_SITE_NOT_FOUND: "That site doesn't exist",
  CUSTOMER_SITE_CREATION_FAILED: "Couldn't create the site",
  CUSTOMER_SITE_UPDATE_FAILED: "Couldn't update the site",
  CUSTOMER_SITE_DELETION_FAILED: "Couldn't delete the site",
  CUSTOMER_SITE_INVALID_ID: 'Invalid site ID',
  CUSTOMER_SITE_PRIMARY_SET_FAILED: "Couldn't set primary site",
  CUSTOMER_NOTE_NOT_FOUND: "That note doesn't exist",
  CUSTOMER_NOTE_CREATION_FAILED: "Couldn't create the note",
  CUSTOMER_NOTE_DELETION_FAILED: "Couldn't delete the note",
  CUSTOMER_NOTE_INVALID_ID: 'Invalid note ID',
  CUSTOMER_RELATIONSHIP_NOT_FOUND: "That relationship doesn't exist",
  CUSTOMER_RELATIONSHIP_CREATION_FAILED: "Couldn't create the relationship",
  CUSTOMER_RELATIONSHIP_DELETION_FAILED: "Couldn't delete the relationship",
  CUSTOMER_RELATIONSHIP_INVALID_ID: 'Invalid relationship ID',
  CUSTOMER_ACTIVITY_CREATION_FAILED: "Couldn't log the activity",
  CUSTOMER_ACTIVITY_LOADING_FAILED: "Couldn't load activities",
  CUSTOMER_PREFERENCES_UPDATE_FAILED: "Couldn't update preferences",
  CUSTOMER_CUSTOM_FIELDS_UPDATE_FAILED: "Couldn't update custom fields",
  CUSTOMER_APPROVAL_REQUEST_CREATION_FAILED: "Couldn't create approval request",
  CUSTOMER_SEARCH_QUERY_REQUIRED: 'Search query is required',
  CUSTOMER_SEARCH_FAILED: 'Search failed',
  CUSTOMER_USER_SYNC_FAILED: 'User sync failed',
  CUSTOMER_USER_ID_REQUIRED: 'User ID is required',
  CUSTOMER_GRAPH_TOKEN_SERVICE_UNAVAILABLE: 'Graph token service is unavailable',
  CUSTOMER_DATABASE_POOL_NOT_CONFIGURED: 'Database pool is not configured',

  // ── Asset Lifecycle (Assets) ──────────────────────────────────────────────
  ASSET_LIFECYCLE_CERTIFICATE_NOT_FOUND: "That certificate doesn't exist",
  ASSET_LIFECYCLE_CERTIFICATE_CREATION_FAILED: "Couldn't create the certificate",
  ASSET_LIFECYCLE_CERTIFICATE_UPDATE_FAILED: "Couldn't update the certificate",
  ASSET_LIFECYCLE_CERTIFICATE_DELETION_FAILED: "Couldn't delete the certificate",
  ASSET_LIFECYCLE_CERTIFICATE_RENEWAL_FAILED: 'Certificate renewal failed',
  ASSET_LIFECYCLE_CERTIFICATE_CHECK_FAILED: 'Certificate check failed',
  ASSET_LIFECYCLE_DOMAIN_NOT_FOUND: "That domain doesn't exist",
  ASSET_LIFECYCLE_DOMAIN_REQUIRED: 'Domain is required',
  ASSET_LIFECYCLE_DOMAIN_SCAN_FAILED: 'Domain scan failed',
  ASSET_LIFECYCLE_LICENSE_NOT_FOUND: "That license doesn't exist",
  ASSET_LIFECYCLE_LICENSE_CREATION_FAILED: "Couldn't create the license",
  ASSET_LIFECYCLE_LICENSE_UPDATE_FAILED: "Couldn't update the license",
  ASSET_LIFECYCLE_LICENSE_DELETION_FAILED: "Couldn't delete the license",
  ASSET_LIFECYCLE_LICENSE_ASSIGNMENT_FAILED: "Couldn't assign the license",
  ASSET_LIFECYCLE_LICENSE_UNASSIGNMENT_FAILED: "Couldn't unassign the license",
  ASSET_LIFECYCLE_ACME_ORDER_CREATION_FAILED: 'ACME order creation failed',
  ASSET_LIFECYCLE_ACME_CHALLENGE_INVALID: 'ACME challenge is invalid',
  ASSET_LIFECYCLE_ACME_VERIFICATION_FAILED: 'ACME verification failed',
  ASSET_LIFECYCLE_ACME_FINALIZATION_FAILED: 'ACME finalization failed',
  ASSET_LIFECYCLE_RUNBOOK_NOT_FOUND: "That runbook doesn't exist",
  ASSET_LIFECYCLE_RUNBOOK_CREATION_FAILED: "Couldn't create the runbook",
  ASSET_LIFECYCLE_RUNBOOK_UPDATE_FAILED: "Couldn't update the runbook",
  ASSET_LIFECYCLE_RUNBOOK_DELETION_FAILED: "Couldn't delete the runbook",
  ASSET_LIFECYCLE_RUNBOOK_PUBLICATION_FAILED: "Couldn't publish the runbook",
  ASSET_LIFECYCLE_RUNBOOK_EXECUTION_FAILED: 'Runbook execution failed',
  ASSET_LIFECYCLE_RUNBOOK_RENDER_FAILED: 'Runbook rendering failed',
  ASSET_LIFECYCLE_SITEDOC_NOT_FOUND: "That site document doesn't exist",
  ASSET_LIFECYCLE_SITEDOC_GENERATION_FAILED: 'Site document generation failed',
  ASSET_LIFECYCLE_SITEDOC_EXPORT_FAILED: 'Site document export failed',
  ASSET_LIFECYCLE_SITEDOC_SERVICE_NOT_CONFIGURED: 'Site documentation service is not configured',
  ASSET_LIFECYCLE_CREDENTIAL_VALIDATION_FAILED: 'Credential validation failed',
  ASSET_LIFECYCLE_CREDENTIAL_VALIDATION_SERVICE_NOT_CONFIGURED:
    'Credential validation service is not configured',
  ASSET_LIFECYCLE_SUGGESTION_SERVICE_NOT_CONFIGURED: 'Suggestion service is not configured',
  ASSET_LIFECYCLE_UPTIME_MONITOR_NOT_FOUND: "That uptime monitor doesn't exist",
  ASSET_LIFECYCLE_URL_REQUIRED: 'URL is required',

  // ── Vault (Cerberus) ────────────────────────────────────────────
  VAULT_CREDENTIAL_NOT_FOUND: "That credential doesn't exist",
  VAULT_CREDENTIAL_CREATION_FAILED: "Couldn't create the credential",
  VAULT_CREDENTIAL_UPDATE_FAILED: "Couldn't update the credential",
  VAULT_CREDENTIAL_DELETION_FAILED: "Couldn't delete the credential",
  VAULT_CREDENTIAL_DECRYPTION_FAILED: "Couldn't decrypt the credential",
  VAULT_CREDENTIAL_ENCRYPTION_FAILED: "Couldn't encrypt the credential",
  VAULT_FOLDER_NOT_FOUND: "That vault folder doesn't exist",
  VAULT_FOLDER_CREATION_FAILED: "Couldn't create the folder",
  VAULT_FOLDER_UPDATE_FAILED: "Couldn't update the folder",
  VAULT_FOLDER_DELETION_FAILED: "Couldn't delete the folder",
  VAULT_SHARE_CREATION_FAILED: "Couldn't share the credential",
  VAULT_SHARE_REVOCATION_FAILED: "Couldn't revoke the share",
  VAULT_ACCESS_DENIED: "You don't have access to this vault item",
  VAULT_LOCKED: 'The vault is locked',
  VAULT_INITIALIZATION_FAILED: 'Vault initialization failed',
  VAULT_KEY_ROTATION_FAILED: 'Vault key rotation failed',

  // ── Compliance ───────────────────────────────────────────────────
  COMPLIANCE_NATS_UNAVAILABLE: 'Compliance messaging service is unavailable',
  COMPLIANCE_DEVICE_NOT_FOUND: 'Device not found for compliance check',
  COMPLIANCE_CUSTOMER_ID_INVALID: 'Invalid customer ID for compliance',
  COMPLIANCE_CUSTOMER_ID_REQUIRED: 'Customer ID is required for compliance',
  COMPLIANCE_DEVICE_ID_INVALID: 'Invalid device ID for compliance',
  COMPLIANCE_DATABASE_ERROR: 'Compliance database error',
  COMPLIANCE_QUERY_ERROR: 'Compliance query error',
  COMPLIANCE_QUERY_FAILED: 'Compliance query failed',
  COMPLIANCE_TENANT_HEADER_MISSING: 'Tenant header is required for compliance',

  // ── Monitoring (Monitoring Alerts) ───────────────────────────────────────────
  SKULD_ALERT_NOT_FOUND: "That alert doesn't exist",
  SKULD_ALERT_CREATION_FAILED: "Couldn't create the alert",
  SKULD_ALERT_UPDATE_FAILED: "Couldn't update the alert",
  SKULD_ALERT_DELETION_FAILED: "Couldn't delete the alert",
  SKULD_ALERT_DEDUPLICATION_FAILED: 'Alert deduplication failed',
  SKULD_ALERT_SUPPRESSION_FAILED: 'Alert suppression failed',
  SKULD_RULE_NOT_FOUND: "That alert rule doesn't exist",
  SKULD_RULE_CREATION_FAILED: "Couldn't create the alert rule",
  SKULD_RULE_UPDATE_FAILED: "Couldn't update the alert rule",
  SKULD_RULE_DELETION_FAILED: "Couldn't delete the alert rule",
  SKULD_DEFINITION_NOT_FOUND: "That alert definition doesn't exist",
  SKULD_THRESHOLD_UPDATE_FAILED: "Couldn't update the threshold",
  SKULD_ESCALATION_FAILED: 'Alert escalation failed',

  // ── Backup Management ──────────────────────────────────────────────────
  BACKUP_VENDOR_NOT_CONFIGURED: 'Backup vendor is not configured',
  BACKUP_JOB_NOT_FOUND: "That backup job doesn't exist",
  BACKUP_JOB_SYNC_FAILED: 'Backup job sync failed',
  BACKUP_ALERT_NOT_FOUND: "That backup alert doesn't exist",
  BACKUP_RESTORE_POINT_NOT_FOUND: "That restore point doesn't exist",
  BACKUP_SCOPE_NOT_FOUND: "That backup scope doesn't exist",
  BACKUP_SCOPE_CREATION_FAILED: "Couldn't create the backup scope",
  BACKUP_SCOPE_UPDATE_FAILED: "Couldn't update the backup scope",
  BACKUP_TEST_EXECUTION_FAILED: 'Restore test failed',
  BACKUP_TEST_RESULT_FETCH_FAILED: "Couldn't load test results",
  BACKUP_COVERAGE_CHECK_FAILED: 'Backup coverage check failed',

  // ── Signatures (EmailSignatures) ───────────────────────────────────────────
  EMAIL_SIGNATURES_CAMPAIGN_NOT_FOUND: "That signature campaign doesn't exist",
  EMAIL_SIGNATURES_CAMPAIGN_CREATION_FAILED: "Couldn't create the campaign",
  EMAIL_SIGNATURES_CAMPAIGN_UPDATE_FAILED: "Couldn't update the campaign",
  EMAIL_SIGNATURES_CAMPAIGN_DELETION_FAILED: "Couldn't delete the campaign",
  EMAIL_SIGNATURES_TEMPLATE_CREATION_FAILED: "Couldn't create the signature template",
  EMAIL_SIGNATURES_TEMPLATE_UPDATE_FAILED: "Couldn't update the signature template",
  EMAIL_SIGNATURES_TEMPLATE_DELETION_FAILED: "Couldn't delete the signature template",
  EMAIL_SIGNATURES_RENDERING_FAILED: 'Signature rendering failed',
  EMAIL_SIGNATURES_FONT_UPLOAD_FAILED: 'Font upload failed',
  EMAIL_SIGNATURES_FONT_DELETION_FAILED: "Couldn't delete the font",
  EMAIL_SIGNATURES_BRANDING_UPDATE_FAILED: "Couldn't update branding",

  // ── Automation (Automation Scripts) ─────────────────────────────────────────────
  TYR_WORKFLOW_NOT_FOUND: "That workflow doesn't exist",
  TYR_WORKFLOW_CREATION_FAILED: "Couldn't create the workflow",
  TYR_WORKFLOW_UPDATE_FAILED: "Couldn't update the workflow",
  TYR_WORKFLOW_DELETION_FAILED: "Couldn't delete the workflow",
  TYR_WORKFLOW_EXECUTION_FAILED: 'Workflow execution failed',
  TYR_WORKFLOW_PAUSE_FAILED: "Couldn't pause the workflow",
  TYR_WORKFLOW_RESUME_FAILED: "Couldn't resume the workflow",
  TYR_ACTION_NOT_FOUND: "That action doesn't exist",
  TYR_ACTION_EXECUTION_FAILED: 'Action execution failed',
  TYR_TRIGGER_NOT_FOUND: "That trigger doesn't exist",
  TYR_CONDITION_EVALUATION_FAILED: 'Condition evaluation failed',
  TYR_APPROVAL_NOT_FOUND: "That approval request doesn't exist",
  TYR_APPROVAL_CREATION_FAILED: "Couldn't create the approval request",
  TYR_APPROVAL_RESPONSE_FAILED: "Couldn't respond to the approval",
  TYR_SCRIPT_NOT_FOUND: "That script doesn't exist",
  TYR_SCRIPT_CREATION_FAILED: "Couldn't create the script",
  TYR_SCRIPT_UPDATE_FAILED: "Couldn't update the script",
  TYR_SCRIPT_DELETION_FAILED: "Couldn't delete the script",
  TYR_SCRIPT_EXECUTION_FAILED: 'Script execution failed',

  // ── Remaining modules (shorter) ──────────────────────────────────
  COMMAND_NOT_FOUND: "That command doesn't exist",
  COMMAND_CREATION_FAILED: "Couldn't create the command",
  COMMAND_EXECUTION_FAILED: 'Command execution failed',
  COMMAND_CANCELLATION_FAILED: "Couldn't cancel the command",
  COMMAND_BROADCAST_FAILED: "Couldn't broadcast the command",
  COMMAND_BATCH_CREATION_FAILED: "Couldn't create the batch command",
  COMMAND_REGISTRY_WRITE_FAILED: 'Registry write failed',
  COMMAND_SERVICE_CONTROL_FAILED: 'Service control failed',
  COMMAND_SCRIPT_EXECUTION_FAILED: 'Script execution failed',
  COMMAND_HARDWARE_COLLECTION_FAILED: 'Hardware collection failed',
  PATCH_NOT_FOUND: "That patch doesn't exist",
  PATCH_SCAN_INITIATION_FAILED: "Couldn't start the patch scan",
  PATCH_APPROVAL_FAILED: 'Patch approval failed',
  PATCH_INSTALLATION_FAILED: 'Patch installation failed',
  PATCH_POLICY_NOT_FOUND: "That patch policy doesn't exist",
  PATCH_POLICY_CREATION_FAILED: "Couldn't create the patch policy",
  PATCH_POLICY_UPDATE_FAILED: "Couldn't update the patch policy",
  PATCH_POLICY_DELETION_FAILED: "Couldn't delete the patch policy",
  PATCH_CVE_LOOKUP_FAILED: 'CVE lookup failed',
  FLEET_DEPLOY_FAILED: 'Agent deployment failed',
  FLEET_PACKAGE_NOT_FOUND: "That agent package doesn't exist",
  FLEET_CUTOVER_FAILED: 'Agent cutover failed',
  FLEET_RESULTS_FETCH_FAILED: "Couldn't load deployment results",
  FLEET_UNINSTALL_FAILED: 'Agent uninstall failed',
  FLEET_ROLLBACK_FAILED: 'Agent rollback failed',
  COMMUNICATIONS_MIGRATION_SEND_FAILED: "Couldn't send the email",
  COMMUNICATIONS_MIGRATION_TEMPLATE_NOT_FOUND: "That email template doesn't exist",
  COMMUNICATIONS_MIGRATION_TEMPLATE_RENDERING_FAILED: 'Email template rendering failed',
  COMMUNICATIONS_MIGRATION_CALLFLOW_NOT_FOUND: "That call flow doesn't exist",
  COMMUNICATIONS_MIGRATION_CALLFLOW_CREATION_FAILED: "Couldn't create the call flow",
  COMMUNICATIONS_MIGRATION_CALLFLOW_UPDATE_FAILED: "Couldn't update the call flow",
  COMMUNICATIONS_MIGRATION_CALLFLOW_DELETION_FAILED: "Couldn't delete the call flow",
  COMMUNICATIONS_MIGRATION_MIGRATION_BATCH_NOT_FOUND: "That migration batch doesn't exist",
  COMMUNICATIONS_MIGRATION_MIGRATION_INITIATION_FAILED: "Couldn't start the migration",
  PROJECT_DELIVERY_PROJECT_NOT_FOUND: "That project doesn't exist",
  PROJECT_DELIVERY_PROJECT_CREATION_FAILED: "Couldn't create the project",
  PROJECT_DELIVERY_PROJECT_UPDATE_FAILED: "Couldn't update the project",
  PROJECT_DELIVERY_PROJECT_DELETION_FAILED: "Couldn't delete the project",
  PROJECT_DELIVERY_TEMPLATE_NOT_FOUND: "That project template doesn't exist",
  PROJECT_DELIVERY_RESOURCE_CREATION_FAILED: "Couldn't add the resource",
  PROJECT_DELIVERY_BUDGET_UPDATE_FAILED: "Couldn't update the budget",
  PROJECT_DELIVERY_SCHEDULE_UPDATE_FAILED: "Couldn't update the schedule",
  PROJECT_DELIVERY_RISK_CREATION_FAILED: "Couldn't add the risk",
  PROJECT_DELIVERY_ISSUE_CREATION_FAILED: "Couldn't add the issue",
  PROCUREMENT_PRODUCT_NOT_FOUND: "That product doesn't exist",
  PROCUREMENT_CART_EMPTY: 'Cart is empty',
  PROCUREMENT_ORDER_NOT_FOUND: "That order doesn't exist",
  PROCUREMENT_ORDER_APPROVAL_FAILED: 'Order approval failed',
  PROCUREMENT_ORDER_CANCELLATION_FAILED: 'Order cancellation failed',
  PROCUREMENT_MARGIN_NOT_FOUND: "That margin rule doesn't exist",
  PROCUREMENT_BUNDLE_NOT_FOUND: "That bundle doesn't exist",
  CONFIGURATION_DRIFT_BASELINE_NOT_FOUND: "That baseline doesn't exist",
  CONFIGURATION_DRIFT_SCAN_FAILED: 'Drift scan failed',
  CONFIGURATION_DRIFT_SNAPSHOT_NOT_FOUND: "That snapshot doesn't exist",
  CONFIGURATION_DRIFT_REMEDIATION_FAILED: 'Drift remediation failed',
  CONFIGURATION_DRIFT_POLICY_NOT_FOUND: "That drift policy doesn't exist",
  CONFIGURATION_DRIFT_POLICY_CREATION_FAILED: "Couldn't create the drift policy",
  CONFIGURATION_DRIFT_SCAN_NOT_FOUND: "That drift scan doesn't exist",
  CONFIGURATION_DRIFT_RECORD_NOT_FOUND: "That drift record doesn't exist",
  CONFIGURATION_DRIFT_REMEDIATION_JOB_NOT_FOUND: "That remediation job doesn't exist",
  CONFIGURATION_DRIFT_ROLLBACK_NOT_FOUND: "That rollback operation doesn't exist",
  CONFIGURATION_DRIFT_COLLECTOR_NOT_FOUND: "That drift collector doesn't exist",
  CONFIGURATION_DRIFT_REPORT_NOT_FOUND: "That drift report doesn't exist",
  CLOUDKICK_TEMPLATE_NOT_FOUND: "That CloudKick template doesn't exist",
  CLOUDKICK_TEMPLATE_CREATION_FAILED: "Couldn't create the template",
  CLOUDKICK_DEPLOY_FAILED: 'CloudKick deployment failed',
  CLOUDKICK_ITEM_NOT_FOUND: "That CloudKick item doesn't exist",
  CLOUDKICK_IMPORT_FAILED: 'CloudKick import failed',
  HESTIA_SETUP_INCOMPLETE: 'Setup is not complete',
  HESTIA_CATEGORY_NOT_FOUND: "That setup category doesn't exist",
  HESTIA_ASSESSMENT_FAILED: 'Setup assessment failed',
  HESTIA_REMEDIATION_FAILED: 'Setup remediation failed',
  IDENTITY_LIFECYCLE_MEMBER_NOT_FOUND: "That member doesn't exist",
  IDENTITY_LIFECYCLE_MEMBER_CREATION_FAILED: "Couldn't create the member",
  IDENTITY_LIFECYCLE_SYNC_FAILED: 'Sync failed',
  IDENTITY_LIFECYCLE_MAPPING_NOT_FOUND: "That mapping doesn't exist",
  TENANT_ADMINISTRATION_STAFF_NOT_FOUND: "That staff member doesn't exist",
  TENANT_ADMINISTRATION_STAFF_CREATION_FAILED: "Couldn't create the staff member",
  TENANT_ADMINISTRATION_STAFF_UPDATE_FAILED: "Couldn't update the staff member",
  TENANT_ADMINISTRATION_STAFF_DELETION_FAILED: "Couldn't delete the staff member",
  TENANT_ADMINISTRATION_SKILL_NOT_FOUND: "That skill doesn't exist",
  TENANT_ADMINISTRATION_CERTIFICATION_NOT_FOUND: "That certification doesn't exist",
  PERSONA_NOT_FOUND: "That persona doesn't exist",
  PERSONA_CREATION_FAILED: "Couldn't create the persona",
  PERSONA_UPDATE_FAILED: "Couldn't update the persona",
  PERSONA_DELETION_FAILED: "Couldn't delete the persona",
  PERSONA_MAILBOX_CREATION_FAILED: "Couldn't create the persona mailbox",
  PERSONA_EXECUTION_FAILED: 'Persona execution failed',
  MJOLNIR_PROFILE_NOT_FOUND: "That login profile doesn't exist",
  MJOLNIR_SESSION_NOT_FOUND: "That browser session doesn't exist",
  MJOLNIR_LOGIN_FAILED: 'Automated login failed',
  MJOLNIR_CONSENT_FAILED: 'Automated consent failed',
  MJOLNIR_TEMPLATE_NOT_FOUND: "That login template doesn't exist",
  REPORTS_GENERATION_FAILED: 'Report generation failed',
  REPORTS_EXPORT_FAILED: 'Report export failed',
  REPORTS_SCHEDULE_NOT_FOUND: "That report schedule doesn't exist",
  REPORTS_DEFINITION_NOT_FOUND: "That report definition doesn't exist",
  FILETRANSFER_NOT_FOUND: "That file transfer doesn't exist",
  FILETRANSFER_INITIATION_FAILED: "Couldn't start the file transfer",
  FILETRANSFER_CHUNK_UPLOAD_FAILED: 'File chunk upload failed',
  FILETRANSFER_COMPLETION_FAILED: 'File transfer completion failed',
  FILETRANSFER_VERIFICATION_FAILED: 'File transfer verification failed',
  MIGRATION_PROFILE_NOT_FOUND: "That migration profile doesn't exist",
  MIGRATION_PROFILE_CREATION_FAILED: "Couldn't create the migration profile",
  MIGRATION_PREFLIGHT_FAILED: 'Migration preflight check failed',
  MIGRATION_EXPORT_FAILED: 'Migration export failed',
  MIGRATION_IMPORT_FAILED: 'Migration import failed',
  MIGRATION_VALIDATION_FAILED: 'Migration validation failed',
  TIMEENTRY_NOT_FOUND: "That time entry doesn't exist",
  TIMEENTRY_CREATION_FAILED: "Couldn't create the time entry",
  TIMEENTRY_UPDATE_FAILED: "Couldn't update the time entry",
  TIMEENTRY_DELETION_FAILED: "Couldn't delete the time entry",
  GRAPH_API_CALL_FAILED: 'Microsoft Graph API call failed',
  GRAPH_TOKEN_ACQUISITION_FAILED: "Couldn't acquire Graph API token",
  GRAPH_TOKEN_REFRESH_FAILED: "Couldn't refresh Graph API token",
  GRAPH_CONSENT_REQUIRED: 'Admin consent is required for this M365 operation',
  GRAPH_TENANT_NOT_FOUND: 'M365 tenant not found',
  M365_DOMAIN_NOT_FOUND: 'M365 domain not found',
  M365_LICENSE_SYNC_FAILED: 'M365 license sync failed',
  M365_USER_SYNC_FAILED: 'M365 user sync failed',
  M365_GROUP_SYNC_FAILED: 'M365 group sync failed',
  INTUNE_SYNC_FAILED: 'Intune sync failed',
  SHAREPOINT_SYNC_FAILED: 'SharePoint sync failed',
  TEAMS_SYNC_FAILED: 'Teams sync failed',
  VENDOR_PROFILE_NOT_FOUND: "That vendor profile doesn't exist",
  VENDOR_DEPLOY_FAILED: 'Vendor deployment failed',
  VENDOR_SYNC_FAILED: 'Vendor sync failed',
  VENDOR_HEALTH_CHECK_FAILED: 'Vendor health check failed',
  VENDOR_PROVIDER_UNAVAILABLE: 'Vendor provider is unavailable',
  VENDOR_CUSTOMER_DOMAIN_INVALID:
    'Configure a valid routable Microsoft 365 domain for this client before provisioning vendors',
  WEBHOOK_NOT_FOUND: "That webhook doesn't exist",
  WEBHOOK_CREATION_FAILED: "Couldn't create the webhook",
  WEBHOOK_DELIVERY_FAILED: 'Webhook delivery failed',
  WEBHOOK_VALIDATION_FAILED: 'Webhook validation failed',
  SETTINGS_FETCH_FAILED: "Couldn't load settings",
  SETTINGS_UPDATE_FAILED: "Couldn't update settings",
  BRANDING_UPDATE_FAILED: "Couldn't update branding",
  BUSINESS_HOURS_FETCH_FAILED: "Couldn't load business hours",
  BUSINESS_HOURS_UPDATE_FAILED: "Couldn't update business hours",
  CONFIG_DRIFT_SCAN_FAILED: 'Configuration drift scan failed',
  CONFIG_DRIFT_REMEDIATION_FAILED: 'Configuration drift remediation failed',
  AUDIT_LOG_CREATION_FAILED: "Couldn't create audit log entry",
  AUDIT_LOG_FETCH_FAILED: "Couldn't load audit log",
  NOTIFICATION_CREATION_FAILED: "Couldn't create the notification",
  NOTIFICATION_DISPATCH_FAILED: "Couldn't send the notification",
  PERMISSION_INSUFFICIENT: 'Insufficient permissions',
  SUBSCRIPTION_REQUIRED: 'A subscription is required',
  TEMPLATE_NOT_FOUND: "That template doesn't exist",
  TEMPLATE_RENDERING_FAILED: 'Template rendering failed',
  CREDENTIAL_HEALTH_SCAN_FAILED: "Couldn't load or start the credential health scan",
  CREDENTIAL_HEALTH_STATE_FAILED: "Couldn't load credential health",
  CREDENTIAL_HEALTH_TEST_FAILED: 'Credential test failed',
  CREDENTIAL_HEALTH_ROTATION_FAILED: "Couldn't update credential rotation state",
  APPSTORE_APP_NOT_FOUND: "That app doesn't exist",
  APPSTORE_INSTALL_FAILED: 'App installation failed',
  APPSTORE_UNINSTALL_FAILED: 'App uninstallation failed',
  APPSTORE_CONFIG_FAILED: 'App configuration failed',
  DISRUPTION_NOT_FOUND: "That disruption doesn't exist",
  DISRUPTION_CREATION_FAILED: "Couldn't create the disruption",
  DISRUPTION_UPDATE_FAILED: "Couldn't update the disruption",
  HYGIENE_SCAN_FAILED: 'Hygiene scan failed',
  HYGIENE_RECOMMENDATION_FETCH_FAILED: "Couldn't load hygiene recommendations",
  SCHEDULER_TASK_NOT_FOUND: "That scheduled task doesn't exist",
  SCHEDULER_TASK_CREATION_FAILED: "Couldn't create the scheduled task",
  SCHEDULER_TASK_EXECUTION_FAILED: 'Scheduled task execution failed',
  RATELIMIT_CONFIG_FETCH_FAILED: "Couldn't load rate limit config",
  RATELIMIT_CONFIG_UPDATE_FAILED: "Couldn't update rate limit config",
  PLAYBOOK_NOT_FOUND: "That playbook doesn't exist",
  PLAYBOOK_CREATION_FAILED: "Couldn't create the playbook",
  PLAYBOOK_UPDATE_FAILED: "Couldn't update the playbook",
  PLAYBOOK_EXECUTION_FAILED: 'Playbook execution failed',
  TRUSTSTREAM_REPORT_NOT_FOUND: "That trust report doesn't exist",
  TRUSTSTREAM_GENERATION_FAILED: 'Trust report generation failed',
  TRUSTSTREAM_SHARING_FAILED: "Couldn't share the trust report",
  CUSTOMER_LIFECYCLE_PROPOSAL_NOT_FOUND: "That proposal doesn't exist",
  CUSTOMER_LIFECYCLE_PROPOSAL_CREATION_FAILED: "Couldn't create the proposal",
  CUSTOMER_LIFECYCLE_QUOTE_NOT_FOUND: "That quote doesn't exist",
  CUSTOMER_LIFECYCLE_QUOTE_CREATION_FAILED: "Couldn't create the quote",
  CUSTOMER_LIFECYCLE_PIPELINE_NOT_FOUND: "That pipeline doesn't exist",
  CUSTOMER_LIFECYCLE_ESTIMATOR_NOT_FOUND: "That estimate doesn't exist",
  CUSTOMER_LIFECYCLE_DAY1_NOT_FOUND: "That Day 1 plan doesn't exist",
  CUSTOMER_LIFECYCLE_CONTENT_NOT_FOUND: "That content block doesn't exist",
  CUSTOMER_LIFECYCLE_PROVISION_TEMPLATE_REQUIRED:
    'Choose a published Runway template for this provisioning task',
  CUSTOMER_LIFECYCLE_PROVISION_APPROVAL_REQUIRED:
    'Confirm the exact Runway template and provide an approval reason',
  CUSTOMER_LIFECYCLE_PROVISION_RUNWAY_UNAVAILABLE: 'Runway provisioning is temporarily unavailable',
  CUSTOMER_LIFECYCLE_PROVISION_LAUNCH_BLOCKED:
    'Runway launch was blocked by template policy or current prerequisite evidence',
  QUICKBOOKS_CONNECTION_FAILED: 'QuickBooks connection failed',
  QUICKBOOKS_SYNC_FAILED: 'QuickBooks sync failed',
  QUICKBOOKS_INVOICE_PUSH_FAILED: "Couldn't push invoice to QuickBooks",
  QUICKBOOKS_TOKEN_REFRESH_FAILED: 'QuickBooks token refresh failed',
  XERO_CONNECTION_FAILED: 'Xero connection failed',
  XERO_SYNC_FAILED: 'Xero sync failed',
  XERO_INVOICE_PUSH_FAILED: "Couldn't push invoice to Xero",
  XERO_TOKEN_REFRESH_FAILED: 'Xero token refresh failed',
};
